/// <reference types="@angular/localize" />

import { enableProdMode, importProvidersFrom, provideAppInitializer, inject } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';

import { AppComponent } from './app/app.component';
import { AppRoutingModule } from './app/app-routing.module';
import { SharedModule } from './app/shared/shared.module';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GoogleMapsModule } from '@angular/google-maps';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { NgbRatingModule, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { NgxMaskModule } from 'ngx-mask';
import { ToastrModule } from 'ngx-toastr';
import { NgxDocViewerModule } from 'ngx-doc-viewer';

import { environment } from './environments/environment';

import { ToastComponent } from './app/shared/components/toast/toast.component';

// Interceptors & Guards
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { TimeZoneInterceptor } from './app/core/interceptors/timezone-interceptor.service';
import { AuthInterceptor } from './app/core/interceptors/auth-interceptor.service';
import { ReferenceDataCacheInterceptor } from './app/core/interceptors/reference-data-cache.interceptor';
import { AuthenticationGuard } from './app/core/guards/auth-guard';
import { PermissionGuard } from './app/core/guards/pemission-guard';
import { NavigationHistoryService } from './app/shared/services/navigation-history.service';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { ES_AR_DATE_FORMATS, EsArDateAdapter } from './app/shared/adapters/es-ar-date-adapter';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

// Pipes
import { DatePipe, TitleCasePipe } from '@angular/common';

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      AuthModule.forRoot({
        ...environment.auth,
        // scope: env.auth.scope,
        // audience: env.auth.audience,
        useRefreshTokens: true,
        cacheLocation: 'localstorage',
        httpInterceptor: {
          allowedList: [
            `${environment.SupplierApiUrl}api/*`,
            `${environment.WorkloadsApiUrl}api/*`,
            `${environment.ManufacturingApiUrl}api/*`,
            `${environment.GeolocationApiUrl}api/*`,
            `${environment.NotificationApiUrl}api/*`,
            `${environment.UserManagement}api/*`,
            `${environment.ProductApiUrl}api/*`,
            `${environment.ActivityLogsApiUrl}api/*`,
            `${environment.faqsApiUrl}api/*`,
            `${environment.fileServerApiUrl}api/*`,
          ],
        },
      }),
      BrowserModule,
      AppRoutingModule,
      SharedModule,
      FormsModule,
      ReactiveFormsModule,
      MatDialogModule,
      MatSlideToggleModule,
      MatTabsModule,
      MatTooltipModule,
      GoogleMapsModule,
      MatMenuModule,
      MatButtonModule,
      NgbRatingModule,
      NgbModule,
      NgxChartsModule,
      NgxMaterialTimepickerModule,
      NgxMaskModule.forRoot({ showMaskTyped: true }),
      ToastrModule.forRoot({
        toastComponent: ToastComponent // Podés reemplazar por ToastComponent si querés
      }),
      NgxDocViewerModule,
    ),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    // First: a cache hit or a shared in-flight request skips token lookup and the network.
    { provide: HTTP_INTERCEPTORS, useClass: ReferenceDataCacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TimeZoneInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    AuthenticationGuard,
    PermissionGuard,
    // Typed dates are day/month/year in every datepicker (after importProvidersFrom, so it wins over MatNativeDateModule).
    { provide: MAT_DATE_LOCALE, useValue: 'es-AR' },
    { provide: DateAdapter, useClass: EsArDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: ES_AR_DATE_FORMATS },
    // Design system: every mat-form-field without an explicit appearance renders as outline.
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    DatePipe,
    TitleCasePipe,
    // "Volver" needs every page of this tab's history, also the ones opened before the first back bar was shown.
    provideAppInitializer(() => {
      inject(NavigationHistoryService);
    }),
  ]
});
